import Card from "../../../../Components/Card/Card";
import {ReadOnlyField} from "../../../../Components/FormComponents/Input/Input";
import React, {useEffect, useState} from "react";
import Modals from "../../../../Constants/modals";
import {useTranslation} from "react-i18next";
import {Button, ButtonDirection, ButtonOption} from "../../../../Components/Fundamentals/Button";
import Tooltip, {TooltipContent, TooltipTrigger} from "../../../../Components/Overlays/Tooltip";
import useModal from "../../../../Hooks/useModal";
import {useDispatch, useSelector} from "react-redux";
import {InfoLabel} from "../../../../Components/Badges/InfoLabel";
import dateUtils from "../../../../Utils/dateUtils";
import {VossSubscriptionBillingPeriodKind} from "../../../../Constants/VOSS/vossSubscriptionBillingPeriodKind";
import customerRoutes from "../../../../Constants/Customer/customerRoutes";
import {useNavigate} from "react-router-dom";
import subscriptionUtils from "../../../../Utils/subscriptionUtils";
import {httpFetch} from "../../../../Utils/httpUtils";

export const RankDirection = {
    LOWER: 0,
    HIGHER: 1
};

export const HeaderButton = ({ text, onClick, disabled, tooltipText }) => {
    const button = (
        <Button option={ButtonOption.DESTRUCTIVE} disabled={disabled} onClick={onClick}>
            {text}
        </Button>
    );

    if (tooltipText) {
        return (
            <Tooltip>
                <TooltipTrigger>{button}</TooltipTrigger>
                <TooltipContent>{tooltipText}</TooltipContent>
            </Tooltip>
        );
    }

    return button;
};

export const SubscriptionMainInfo = ({ isLoading, blockEdit }) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {openModal} = useModal();
    const dispatch = useDispatch();

    const customer = useSelector((state) => state.CustomerReducer.customer);
    const subscription = useSelector((state) => state.SubscriptionReducer.subscription);
    const subscriptions = useSelector((state) => state.CustomerReducer.subscriptions);
    const vossInvoices = useSelector((state) => state.CustomerReducer.vossCustomerInvoices);

    const availableMigration = useSelector((state) => state.SubscriptionReducer.availableMigration);
    const subscriptionHasCollaboration = subscription ? subscriptionUtils.isInCollaboration(subscription) : false;
    const hasScheduledTermination = Boolean(subscription?.termination);
    const lowerRankDependentSubscription = subscriptionUtils.findDependentByRank(subscriptions, subscription, RankDirection.LOWER);

    const [parentSub, setParentSub] = useState(null);
    useEffect(() => {
        if (lowerRankDependentSubscription?.id) {
            httpFetch(`/api/subscriptions/${lowerRankDependentSubscription.id}`, dispatch)
                .then(setParentSub)
                .catch(() => setParentSub(null));
        } else {
            setParentSub(null);
        }
    }, [lowerRankDependentSubscription?.id, dispatch]);

    const lowerRankDependentHasScheduledTermination = lowerRankDependentSubscription && Boolean(lowerRankDependentSubscription.termination);
    const lowerRankDependentHasScheduledChange = parentSub?.scheduledChanges?.length > 0;
    const customerHasUnpaidInvoices = vossInvoices && vossInvoices.length > 0 && vossInvoices.some(invoice => invoice.paymentDueDate && new Date(invoice.paymentDueDate) < new Date());
    const blockTermination = (!!lowerRankDependentSubscription && !lowerRankDependentHasScheduledTermination && !lowerRankDependentHasScheduledChange) || blockEdit || subscriptionHasCollaboration || hasScheduledTermination;

    const hasPostponedBillingDate = subscription?.subscriptionBillingPeriod?.periodKind === VossSubscriptionBillingPeriodKind.NotBillable;
    
    const renderTerminationButton = () => {
        const isDisabled = blockTermination || isLoading;
        const getTooltipText = () => {            
            if (!isDisabled) return null;
            
            if (lowerRankDependentSubscription) {
                return t('editsubscriptioncontainer.lowerTerminationDependencyRestriction', {
                    packageName: lowerRankDependentSubscription.package.name
                });
            }

            if (hasScheduledTermination) {
                return t("editsubscriptioncontainer.subscriptionIsTerminated")
            }

            return t('editsubscriptioncontainer.terminationNotAllowedToSubscription');
        };

        return (
            <HeaderButton
                text={t('editsubscriptioncontainer.cancelagreement')}
                onClick={() => openModal(Modals.SubscriptionTermination)}
                disabled={isDisabled}
                tooltipText={getTooltipText()}
            />
        );
    }

    return (
        <Card>
            <Card.CHeader>
                <Card.Header.Title>
                    {t('subscriptions.mainInfo')}
                    {customerHasUnpaidInvoices &&
                        <div className="d-inline-block position-relative infolabel ml-8">
                            <InfoLabel type={"danger"} title={t("editsubscriptioncontainer.unpaidInvoices")}/>
                        </div>
                    }
                </Card.Header.Title>
                <Card.Header.Buttons>
                    {renderTerminationButton()}
                </Card.Header.Buttons>
            </Card.CHeader>
            <Card.Content isLoading={!subscription}>
                <div className="d-grid grid-cols-4 gap-16">
                    <ReadOnlyField label={t('subscriptions.package')} value={subscription?.package.name}/>
                    <ReadOnlyField label={t('subscriptions.period')} value={subscription?.plan.name}/>
                    {hasPostponedBillingDate ?
                        <Tooltip placement='top' arrowPlacement='top in'>
                            <TooltipTrigger>
                                {<ReadOnlyField icon={"vismaicon-info"} label={t("customersubscriptionperiodcontainer.subscriptionBillingPeriod")}
                                                value={subscriptionUtils.formatPeriodSpan(subscription?.subscriptionBillingPeriod.startDate, subscription?.subscriptionBillingPeriod.endDate, t)}/>}
                            </TooltipTrigger>
                            <TooltipContent>
                                {t("customersubscriptionperiodcontainer.hasPostponedBillingDate", {date: subscriptionUtils.formatPeriodEnd(subscription?.subscriptionBillingPeriod?.endDate, t)})}
                            </TooltipContent>
                        </Tooltip>
                        : <ReadOnlyField label={t("customersubscriptionperiodcontainer.subscriptionBillingPeriod")}
                                         value={subscriptionUtils.formatPeriodSpan(subscription?.subscriptionBillingPeriod?.startDate, subscription?.subscriptionBillingPeriod?.endDate, t)}/>}

                    <ReadOnlyField label={t('subscriptions.purchasedate')} value={dateUtils.formatDate(subscription?.created)}/>
                    <ReadOnlyField label={t('subscriptions.tier')} value={subscription?.tier.name}/>
                    <ReadOnlyField label={t("customersubscriptionperiodcontainer.bindingPeriod")} value={subscriptionUtils.formatPeriodSpan(subscription?.bindingPeriod.startDate, subscription?.periodEnd, t)}/>
                    <ReadOnlyField label={t("customersubscriptionperiodcontainer.usageBillingPeriod")}
                                   value={subscriptionUtils.formatPeriodSpan(subscription?.usageBillingPeriod.startDate, subscription?.usageBillingPeriod.endDate, t)}/>
                    <ReadOnlyField label={t('subscriptions.terminationDate')}
                                   value={subscription?.termination?.terminationDate ? dateUtils.formatDate(subscription?.termination?.terminationDate) : ''}/>
                </div>
            </Card.Content>
            <Card.Footer>
                <Card.Footer.Buttons>
                    {availableMigration && (
                        <Button disabled={!subscription || subscription?.termination}
                                option={ButtonOption.PRIMARY}
                                onClick={() => navigate(customerRoutes.customer(customer.pathId, `${customerRoutes.orderPlacement}?packageId=${availableMigration.id}`))}
                                direction={ButtonDirection.RIGHT}>
                            {t('editsubscriptionform.migrate', { name: availableMigration.name })}
                        </Button>
                    )}
                    <Button disabled={!subscription || subscription?.termination} 
                            option={ButtonOption.PRIMARY}
                            onClick={() => navigate(customerRoutes.customer(customer.pathId, `${customerRoutes.orderPlacement}?packageId=${subscription?.package.id}`))}
                            direction={ButtonDirection.RIGHT}>
                        + {t('editsubscriptionform.upgrade')}
                    </Button>
                </Card.Footer.Buttons>
            </Card.Footer>
        </Card>
    )
}